import { CheckCircle } from "@mui/icons-material";
import { Box, FormControl, FormHelperText, FormLabel, Stack, Typography } from "@mui/joy";
import { CreditRewardCategory } from "griptonite-api-client/types";
import { useController, useFormContext } from "react-hook-form";

import { useCreditProductPresetImagesQuery } from "@/hooks/credits";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import { Media, MediaSelect } from "../forms/MediaInput";
import { RewardFormValues } from "./rewardForms";

// Either one of the category's presets or an upload of the admin's own. The upload slot only shows
// an image the admin uploaded, so picking a preset leaves it empty.
export default function RewardImageInput({
  category,
  presetUuids,
}: {
  category: CreditRewardCategory | null;
  presetUuids: Set<string>;
}) {
  const { control } = useFormContext<RewardFormValues>();
  const { field, fieldState } = useController({ name: "image", control });
  const { data: presets } = useCreditProductPresetImagesQuery();

  const image = field.value as Media | null;
  const selectedUuid = image && "uuid" in image ? image.uuid : null;
  const isPreset = !!selectedUuid && presetUuids.has(selectedUuid);
  const categoryPresets = presets?.filter((preset) => preset.category === category) ?? [];

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Image</FormLabel>
      <Stack gap={1.5}>
        {!category ? (
          <Typography level="body-sm" textColor="neutral.500">
            Choose a category to see its images, or upload your own.
          </Typography>
        ) : categoryPresets.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
              gap: 1,
            }}
          >
            {categoryPresets.map((preset) => {
              const isSelected = preset.image.uuid === selectedUuid;
              return (
                <Box
                  key={preset.image.uuid}
                  component="button"
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => field.onChange(preset.image)}
                  sx={{
                    position: "relative",
                    aspectRatio: "1",
                    padding: 0,
                    borderRadius: "md",
                    overflow: "hidden",
                    cursor: "pointer",
                    backgroundColor: "neutral.100",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.500" : "transparent",
                    outlineOffset: 2,
                    img: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
                  }}
                >
                  <img alt="" src={getThumbnailUrl(preset.image.url)} />
                  {isSelected ? (
                    <CheckCircle
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        color: "primary.500",
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ) : null}
        <Stack gap={0.75}>
          <Typography level="body-sm" textColor="neutral.600">
            Or upload your own
          </Typography>
          <MediaSelect<false>
            media={isPreset ? null : image}
            multiple={false}
            accept="image/*"
            onChange={(media) => field.onChange(media)}
          />
        </Stack>
      </Stack>
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
